import React, { useEffect } from 'react';

import { useSelector, useDispatch } from 'react-redux';
import { Translate } from 'react-localize-redux';
import { push } from 'connected-react-router';

import styled from 'styled-components';
import { rgba } from 'polished';

import Typography from '@components/basics/typography';
import RightArrowIcon from '@components/basics/icons/right-arrow';
import Helper from '@components/basics/helper';
import Flex from '@components/layout/flex';
import AppState from '@webapp/store/state';
import { GetOwnershipsByUserId } from '@webapp/store/ownership/action-creators';
import { GET_OWNERSHIPS_BY_USER_ID } from '@webapp/store/ownership/actions';

import LoadingCtrl from '@webapp/controllers/loading';
import { ReleaseStatus } from '@common/types/release/relase-status';

interface UserDeleteCtrlProps {
  className?: string;
}

export const UserDeleteCtrl: React.FC<UserDeleteCtrlProps> = ({
  className,
}) => {
  const dispatch = useDispatch();
  const activeReleaseStatuses: ReleaseStatus[] = [
    'pending-processing',
    'published',
    'approved',
    'released',
  ];

  const disabled = useSelector((state: AppState) => {
    const userOwnerships = state.ownership?.userOwnerships ?? [];

    const activeReleases = state.catalog.releases.items.filter((r) =>
      activeReleaseStatuses.includes(r.status)
    );

    const activeMerch = state.merch.artistMerch.filter(
      (m) => m.status === 'published' || m.status === 'processing'
    );

    const filteredOwnerships = userOwnerships.filter(
      (o) =>
        activeReleases.some((r) => r.tracks.some((t) => t.id === o.assetId)) ||
        activeMerch.some((m) => m.id === o.assetId)
    );

    return !!filteredOwnerships.length;
  });
  const userId = useSelector((state: AppState) => state.user?.profile?.id);
  const isLoading = useSelector(
    (state: AppState) => !!state.ui.loading[GET_OWNERSHIPS_BY_USER_ID]
  );
  const [hasHelper, setHasHelper] = React.useState(false);

  useEffect(() => {
    if (userId) {
      dispatch(new GetOwnershipsByUserId(userId));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <StyledDeleteButton className={className}>
      <Label className={isLoading || disabled ? 'disabled' : ''} weight="700">
        <Translate id="USER_PROFILE.DEACTIVATE.TITLE" />
      </Label>
      {isLoading ? (
        <LoadingCtrl
          className={`loading-animation ${isLoading ? 'is-loading' : ''}`}
          loading={isLoading}
        />
      ) : disabled ? (
        <Flex
          className="helper-wrapper"
          justifyContent="flex-start"
          alignItems="center"
          gap={5}
        >
          <QuestionMark onClick={() => setHasHelper(!hasHelper)}>
            <Typography weight="700">?</Typography>
          </QuestionMark>
          <Helper onTop textLeft isHover={hasHelper} className="helper">
            User accounts can only be deleted once all published music releases
            and merch products have been taken down.
          </Helper>
        </Flex>
      ) : (
        <ArrowWrapper onClick={() => dispatch(push('/user/deactivate'))}>
          <RightArrowIcon className="icon" />
        </ArrowWrapper>
      )}
    </StyledDeleteButton>
  );
};

export default UserDeleteCtrl;

const StyledDeleteButton = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colors.box};
  padding: 20px;
  width: 100%;

  .helper {
    right: 0;
  }

  .loading-animation {
    position: absolute;
    right: 0;
  }
`;

const ArrowWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  background-color: ${({ theme }) => rgba(theme.colors.accent, 0.1)};
  cursor: pointer;

  .icon {
    width: 9px;

    path {
      fill: ${({ theme }) => theme.colors.accent};
    }
  }
`;

const Label = styled(Typography)`
  color: ${({ theme }) => theme.colors.accent};
  &.disabled {
    color: ${({ theme }) => theme.colors.tertiary};
  }
`;

const QuestionMark = styled.div`
  background-color: ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  height: 24px;
  width: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: help;
  span {
    font-size: ${({ theme }) => theme.typography.small};
    color: ${({ theme }) => theme.colors.background};
  }
`;
